import { BigNumber } from "ethers";
import { useCallback, useState } from "react";
import {
  useCollectionData,
  useDocumentData,
} from "react-firebase-hooks/firestore";
import { useLocalStorage } from "react-use";
import { useRecoilValue } from "recoil";

import { chainIdAtom, listAppSelector } from "../../atoms";
import { parseRawTokenFormValue } from "../../lib/format";
import { appSelector } from "../apps";
import { useFirebase } from "../firebase";
import Flexacoin from "../flexacoin";

export const useChainId = () => useRecoilValue(chainIdAtom);

export const useFormTokenValue = (
  def = ""
): [string, BigNumber, (string) => void] => {
  const [value, setValue] = useState(def);
  const [raw, setRaw] = useState<BigNumber>(parseRawTokenFormValue(def));
  const onEdit = useCallback((value: string) => {
    const raw = parseRawTokenFormValue(value);
    setRaw(raw);
    setValue(value);
  }, []);

  return [value, raw, onEdit];
};

export const useSuppressMigrateModal = () =>
  useLocalStorage("@flexa/capacity/suppress-migrate-modal", false);

export const useBeenHereBefore = () =>
  useLocalStorage("@flexa/capacity/welcomeSeen", false);

export const useEntityTotals = (
  col: string,
  id: string
): [any, boolean, Error | null] => {
  const app = useFirebase();
  return useDocumentData(app?.firestore().collection(col).doc(id));
};

export const useEntitySlices = (col: string, id: string) => {
  const firebase = useFirebase();
  return useCollectionData<any>(
    firebase
      ?.firestore()
      .collection(col)
      .doc(id)
      .collection("hourlySlices")
      .limit(48)
  );
};

const ANNUAL_REWARD = 4_333_248 * 365;

export const useApy = () => {
  const [entity /* , totalsLoading, totalsError */] = useEntityTotals(
    "global",
    "amp"
  );

  const apy = entity?.totalSupply
    ? (
        (ANNUAL_REWARD / Flexacoin.toDecimal(entity?.totalSupply).toNumber()) *
        100
      ).toFixed(2)
    : "0.00";

  return apy;
};

export const useApyByApp = (appId: string) => {
  const globalApy = useApy();

  const app = useRecoilValue(appSelector(appId));
  const appApy = useRecoilValue(listAppSelector(app?.partition ?? ""))?.apy;

  return appApy || globalApy;
};
